import {
  ArrowLeftRight,
  BarChart3,
  Building2,
  Calculator,
  Calendar,
  ChevronDown as ChevronDownIcon,
  DollarSign,
  Filter,
  GraduationCap,
  Hammer,
  Headphones,
  HelpCircle,
  Home,
  Library,
  ListChecks,
  LogOut,
  MapPin,
  Megaphone,
  Menu,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  RefreshCw,
  Search,
  Settings,
  Sparkles,
  User as UserIcon,
  Users,
  UserSquare2,
  Wrench,
  X
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useEntitlementStore } from '@caradasstore/EntitlementStore'
import { useRevertedInvoice } from '@caradasstore/InvoiceEditStore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { useUserStore } from '@caradasstore/UserStore'
import { isClientUser, isTech } from '../../../utils/utils'
import { NAV_MODULES, canUseModule } from '../../../data/entitlements'
import { signOut } from '../../../utils/signOut'
import { AdasIntelligenceLogo } from '@components/adas-intelligence'
import aiHorizontalLogo from '../../../assets/images/Adas_horzontal_white.svg'
import { UserProfile } from '@components/user'
import { AdasBox, AdasCircularProgress, AdasDrawer, AdasMenuList, AdasPaper, AdasTypography } from '@components/wrapper-components'
import { CalibrationCenterBar } from '@components/navigation-bar/calibration-center-bar'
import { convertWorkOrderToInvoice } from 'api/api'
import { useHistory, useLocation } from 'react-router-dom'

import './NavigationBar.css'

const getInitials = (name) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return parts[0][0]?.toUpperCase() || '?'
}

// Routes whose pages wire the mobile-toolbar search/filter store (via `searchOpen` / `filterOpen`).
// Pages not listed here don't consume the icon's state, so showing them would be a dead tap.
const MOBILE_SEARCH_PATHS = [
  '/clients',
  '/products',
  '/quotes',
  '/workorders',
  '/invoices',
  '/support',
  '/help',
  '/adas-intelligence',
  '/manage-location',
  '/manage-users',
  '/network'
]
const MOBILE_FILTER_PATHS = ['/quotes', '/workorders', '/invoices', '/adas-intelligence']

const MobileHamburgerMenu = ({ navigationItems, pathName, handleMenuItemClick, title, centerDetail, setCenterDetail, currentUser }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false)
  const profileRef = useRef(null)
  const userEmail = useUserStore((s) => s.userEmail)
  useEffect(() => {
    if (!profileOpen) return
    const onMouseDown = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [profileOpen])
  const history = useHistory()
  // CAR-4086: the same sign-out as desktop. This used to call authCtx.setLoggedIn, which
  // AuthStore never had, so phone Sign Out threw and the user stayed signed in.
  const handleLogOut = () => {
    setProfileOpen(false)
    signOut(history)
  }
  const toggleSearch = useMobileToolbarStore((s) => s.toggleSearch)
  const toggleFilter = useMobileToolbarStore((s) => s.toggleFilter)
  const pageAction = useMobileToolbarStore((s) => s.pageAction)
  const pageActionOpen = useMobileToolbarStore((s) => s.pageActionOpen)
  const togglePageActionMenu = useMobileToolbarStore((s) => s.togglePageActionMenu)
  const closeAll = useMobileToolbarStore((s) => s.closeAll)

  const toggleDrawer = () => {
    setIsOpen(!isOpen)
  }

  const handleItemClick = (path) => {
    handleMenuItemClick(path)
    setIsOpen(false)
  }

  return (
    <AdasBox
      className='flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 text-white'
      style={{ background: 'var(--primary-gradient)' }}
    >
      <AdasBox className='flex items-center gap-[9.6px] min-w-0'>
        <button onClick={toggleDrawer} aria-label='Open menu' className='p-2 -ml-2 rounded-md hover:bg-white/10 transition-colors'>
          <Menu className='h-5 w-5 text-white' />
        </button>
        <AdasBox className='min-w-0'>
          <AdasTypography className='text-[10px] font-semibold uppercase tracking-widest text-white/60 truncate'>
            {centerDetail?.name || ''}
          </AdasTypography>
          {pathName?.startsWith('/adas-intelligence') ? (
            <img src={aiHorizontalLogo} alt='ADAS Intelligence' className='mt-0.5 h-[19px] w-auto shrink-0' />
          ) : (
            <AdasTypography className='text-[20px] font-bold leading-tight text-white truncate'>{title || ''}</AdasTypography>
          )}
        </AdasBox>
      </AdasBox>
      <AdasBox className='flex items-center gap-2 shrink-0'>
        {/* Pages set pageAction.busy while a menu item is still working (e.g. a PDF
            being generated) so the header can show progress next to the pill. */}
        {pageAction?.busy && <AdasCircularProgress size={16} className='text-white' aria-label='Loading' />}
        {pageAction && Array.isArray(pageAction.items) ? (
          // Dropdown variant — Actions pill with menu
          <AdasBox className='relative'>
            <button
              type='button'
              onClick={togglePageActionMenu}
              disabled={!!pageAction.disabled}
              className='inline-flex items-center gap-1.5 rounded-lg bg-white/15 px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-40'
            >
              {pageAction.label || 'Actions'}
              <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${pageActionOpen ? 'rotate-180' : ''}`} />
            </button>
            {pageActionOpen && pageAction.items.length > 0 && (
              <AdasBox className='absolute right-0 top-full z-30 mt-2 w-[220px] overflow-hidden rounded-lg border border-[#EBEDF0] bg-white py-2 shadow-lg'>
                {pageAction.items.map((it, idx) => (
                  <button
                    key={idx}
                    type='button'
                    onClick={() => {
                      closeAll()
                      it.onClick?.()
                    }}
                    className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] hover:bg-[#F8FAFC]'
                  >
                    {it.icon && <it.icon className='h-4 w-4 text-[#475569]' />}
                    {it.label}
                  </button>
                ))}
              </AdasBox>
            )}
          </AdasBox>
        ) : pageAction ? (
          // Icon-only variant — single tap fires onClick
          <button
            type='button'
            onClick={() => pageAction.onClick?.()}
            disabled={!!pageAction.disabled}
            aria-label={pageAction.label || 'Action'}
            className='inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/30 hover:bg-white/10 transition-colors disabled:cursor-not-allowed disabled:opacity-40'
          >
            {pageAction.icon ? <pageAction.icon className='h-4 w-4 text-white' /> : null}
          </button>
        ) : (
          // Only show the search / filter icons on routes whose pages actually consume them.
          <>
            {MOBILE_SEARCH_PATHS.includes(pathName) && (
              <button
                onClick={toggleSearch}
                aria-label='Search'
                className='p-2 rounded-full border border-white/30 hover:bg-white/10 transition-colors'
              >
                <Search className='h-4 w-4 text-white' />
              </button>
            )}
            {MOBILE_FILTER_PATHS.includes(pathName) && (
              <button
                onClick={toggleFilter}
                aria-label='Filter'
                className='p-2 rounded-full border border-white/30 hover:bg-white/10 transition-colors'
              >
                <Filter className='h-4 w-4 text-white' />
              </button>
            )}
          </>
        )}
        {pathName === '/scheduler' && (currentUser?.is_owner || currentUser?.is_manager || currentUser?.is_admin) && (
          <button
            type='button'
            onClick={() => handleMenuItemClick('/calendar')}
            aria-label='Calendar settings'
            className='inline-flex h-9 w-9 flex-none items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/10'
          >
            <Settings className='h-4 w-4' />
          </button>
        )}
        <AdasBox ref={profileRef} className='relative'>
          <button
            type='button'
            onClick={() => setProfileOpen((o) => !o)}
            aria-label='User menu'
            className='inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-[12px] font-bold text-white hover:bg-white/25 transition-colors'
          >
            {getInitials(currentUser?.name)}
          </button>
          {profileOpen && (
            <AdasBox className='absolute right-0 top-full z-30 mt-2 w-[240px] overflow-hidden rounded-lg border border-[#EBEDF0] bg-white shadow-lg text-left'>
              <AdasBox className='px-4 py-3'>
                <AdasTypography className='text-[14px] font-semibold text-[#0A1628]'>{currentUser?.name || 'User'}</AdasTypography>
                <AdasTypography className='mt-0.5 text-[12px] text-[#6B7280] truncate'>
                  {userEmail || currentUser?.email || ''}
                </AdasTypography>
              </AdasBox>
              <hr className='border-[#EBEDF0]' />
              <AdasBox className='py-1'>
                <button
                  type='button'
                  onClick={() => {
                    setProfileOpen(false)
                    setProfileDrawerOpen(true)
                  }}
                  className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] hover:bg-[#F8FAFC]'
                >
                  <UserIcon className='h-[18px] w-[18px] text-[#475569]' />
                  User Profile
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setProfileOpen(false)
                    window.location.reload()
                  }}
                  className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] hover:bg-[#F8FAFC]'
                >
                  <RefreshCw className='h-[18px] w-[18px] text-[#475569]' />
                  Refresh
                </button>
              </AdasBox>
              <hr className='border-[#EBEDF0]' />
              <AdasBox className='py-1'>
                <button
                  type='button'
                  onClick={handleLogOut}
                  className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-destructive hover:bg-[#F8FAFC]'
                >
                  <LogOut className='h-[18px] w-[18px]' />
                  Sign Out
                </button>
              </AdasBox>
            </AdasBox>
          )}
        </AdasBox>
      </AdasBox>
      <AdasDrawer anchor='left' open={isOpen} onClose={toggleDrawer} className='overflow-y-auto w-[300px]'>
        <AdasBox className='h-full flex flex-col text-white' style={{ background: 'var(--primary-gradient)' }}>
          {/* User header */}
          <AdasBox className='flex items-center justify-between gap-3 px-4 py-4 border-b border-white/10'>
            <AdasBox className='flex items-center gap-3 min-w-0'>
              <AdasBox className='w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-[13px] font-bold text-white shrink-0'>
                {getInitials(currentUser?.name)}
              </AdasBox>
              <AdasBox className='min-w-0'>
                <AdasTypography className='text-[15px] font-semibold leading-tight truncate'>{currentUser?.name || ''}</AdasTypography>
                <AdasTypography className='text-[12px] text-white/60 leading-tight mt-0.5 truncate'>
                  {currentUser?.is_admin ? 'Admin' : 'Technician'}
                </AdasTypography>
              </AdasBox>
            </AdasBox>
            <button
              onClick={toggleDrawer}
              aria-label='Close menu'
              className='inline-flex h-8 w-8 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/20 shrink-0'
            >
              <X className='h-4 w-4' />
            </button>
          </AdasBox>

          {/* Center selector */}
          <AdasBox className='px-4 py-3 border-b border-white/10'>
            <CalibrationCenterBar centerDetail={centerDetail} setCenterDetail={setCenterDetail} inline />
          </AdasBox>

          {/* Menu list */}
          <AdasBox className='flex-1 overflow-y-auto menu-list-container'>
            <AdasMenuList
              variant='withoutClickAway'
              onItemClick={handleItemClick}
              pathName={pathName}
              open={true}
              items={navigationItems}
              isMobile={false}
            />
          </AdasBox>

          {/* Footer */}
          <AdasBox className='border-t border-white/10 px-4 py-4 flex flex-col items-center gap-2'>
            <AdasTypography className='text-[10px] uppercase tracking-widest text-white/40'>Powered by</AdasTypography>
            <AdasIntelligenceLogo width='40%' />
          </AdasBox>
        </AdasBox>
      </AdasDrawer>
      <AdasDrawer anchor='right' open={profileDrawerOpen}>
        <UserProfile user={currentUser} closeDrawer={() => setProfileDrawerOpen(false)} />
      </AdasDrawer>
    </AdasBox>
  )
}

export const NavigationBar = ({ title }) => {
  const location = useLocation()
  const history = useHistory()
  const user = useUserStore((store) => store.currentUser)
  const centerDetail = useCenterStore((store) => store.centerDetail)
  const setCenterDetail = useCenterStore((store) => store.setCenterDetail)
  const activeCcId = useCenterStore((store) => store.ccId)
  const entitlements = useEntitlementStore()
  const invoicePendingToSubmit = useRevertedInvoice((store) => store.revertedInvoice)
  const resetRevertedInvoice = useRevertedInvoice((store) => store.setRevertedInvoice)
  // Desktop sidebar is expanded/collapsed manually via the toggle button in its header
  // (no auto hover behavior). The choice is persisted so it survives reloads/navigation.
  // The mobile drawer always renders expanded — see the AdasDrawer branch above.
  const [isExpanded, setIsExpanded] = useState(() => {
    const stored = localStorage.getItem('nav-sidebar-expanded')
    return stored === null ? true : stored === 'true'
  })
  const open = isExpanded
  const toggleSidebar = () => {
    setIsExpanded((prev) => {
      const next = !prev
      localStorage.setItem('nav-sidebar-expanded', String(next))
      return next
    })
  }

  const isAdmin = !!user?.is_admin
  // Settings is manager/owner/admin only — hide it from techs. The route guards the
  // direct URL as well (see PrivateRoute's blockTechAccess).
  const isTechUser = !!user && isTech(user)
  // A client user reads records; the nav entries that only lead to write screens are
  // dropped. The routes themselves are guarded too (PrivateRoute blockReadOnlyAccess),
  // so hiding these is about not offering a dead end.
  const isReadOnly = isClientUser(user)
  // A client user is confined to its own shop's quotes, work orders, invoices and AI
  // reports - plus VIN Search and the Scheduler, both narrowed to that client by the backend
  // (ClientScopeFilter) - so the other center-wide sections are dropped.

  const navigationItems = [
    { label: 'Home', path: '/', icon: Home, childPathPrefix: '/' },
    { label: 'AI Reports', path: '/adas-intelligence', icon: Sparkles, childPathPrefix: '/adas-intelligence' },
    { label: 'VIN Search', path: '/vinsearch', icon: Search, childPathPrefix: '/vinsearch' },
    { label: 'Quotes', path: '/quotes', icon: Calculator, childPathPrefix: '/quote' },
    { label: 'Work Orders', path: '/workorders', icon: Receipt, childPathPrefix: '/workorder' },
    { label: 'Invoices', path: '/invoices', icon: DollarSign, childPathPrefix: '/invoice' },
    ...(isReadOnly
      ? []
      : [
          { label: 'Clients', path: '/clients', icon: Building2, childPathPrefix: '/client' },
          { label: 'Products', path: '/products', icon: Hammer, childPathPrefix: '/product' }
        ]),
    // Help (center support articles) and Training (the shop staff's Absorb courses) are for the center's own team.
    ...(isReadOnly ? [] : [{ label: 'Help', path: '/help', icon: HelpCircle, childPathPrefix: '/help' }]),
    ...(isReadOnly ? [] : [{ label: 'Support', path: '/support', icon: Headphones, childPathPrefix: '/support' }]),
    ...(isTechUser || isReadOnly ? [] : [{ label: 'Settings', path: '/settings', icon: Settings, childPathPrefix: '/settings' }]),
    // Reporting opens the center's Power BI app - center-wide figures - so client users do not get it.
    ...(isReadOnly ? [] : [{ label: 'Reporting', path: '/reporting', icon: BarChart3 }]),
    ...(isReadOnly ? [] : [{ label: 'Training', path: '/training', icon: GraduationCap }]),
    // CAR-4006: Library links out to the YouTube training playlists, one of which is for body shop
    // clients, so every login gets it.
    { label: 'Library', path: '/app-training', icon: Library, childPathPrefix: '/app-training' }
  ]

  if (isAdmin) {
    navigationItems.push({
      label: 'Admin Tools',
      icon: Wrench,
      children: [
        { label: 'Manage Locations', path: '/manage-location', icon: MapPin, childPathPrefix: '/manage-location' },
        { label: 'Network', path: '/network', icon: Network, childPathPrefix: '/network' },
        { label: 'QWI Search', path: '/qwi-search', icon: Search, childPathPrefix: '/qwi-search' },
        { label: 'Global Directory', path: '/manage-users', icon: Users, childPathPrefix: '/manage-users' },
        { label: 'Global Clients', path: '/global-clients', icon: UserSquare2, childPathPrefix: '/global-clients' },
        { label: 'Broadcast Messages', path: '/broadcast-message', icon: Megaphone, childPathPrefix: '/broadcast-message' },
        { label: 'Calibration Rules', path: '/calibration-rules', icon: ListChecks, childPathPrefix: '/calibration-rules' },
        { label: 'Mappings', path: '/mappings', icon: ArrowLeftRight, childPathPrefix: '/mappings' }
        // Hidden for now (user request 2026-10-27); the pages and their routes stay in place to re-enable.
        // { label: 'Plans & Features', path: '/admin/plans', icon: Layers, childPathPrefix: '/admin/plans' },
        // { label: 'Billing Accounts', path: '/admin/billing-accounts', icon: CreditCard, childPathPrefix: '/admin/billing-accounts' }
      ]
    })
  }

  const submitPendingInvoiceAndNavigate = async (path) => {
    await convertWorkOrderToInvoice({
      workOrderId: invoicePendingToSubmit.id
    })
    resetRevertedInvoice()
    if (path) history.push(path)
  }

  useEffect(() => {
    if (invoicePendingToSubmit && !location.pathname.startsWith('/workorder')) {
      submitPendingInvoiceAndNavigate(null)
    }
    return () => {
      if (invoicePendingToSubmit && location.pathname.startsWith('/invoices')) {
        submitPendingInvoiceAndNavigate(null)
      }
    }
  }, [location, invoicePendingToSubmit])

  const setCenter = useCallback(
    (center) => {
      setCenterDetail(center)
    },
    [setCenterDetail]
  )

  const pathName = location.pathname

  if (centerDetail && !!centerDetail.is_scheduler_enabled) {
    navigationItems.splice(2, 0, { label: 'Scheduler', path: '/scheduler', icon: Calendar, childPathPrefix: '/scheduler' })
  }

  // Reporting, Training, and Admin Tools remain accessible from the top-bar
  // settings gear. Settings is surfaced directly in the sidebar.

  const handleMenuItemClick = (path) => {
    if (path.includes('reporting')) {
      window.open('https://app.powerbi.com/groups/me/apps/03f683aa-953d-461e-ad7a-b722f01d598a?experience=power-bi', '_blank')
    }
    // Exact match: '/app-training' is an in-app page, only '/training' goes out to Absorb.
    if (path === '/training') {
      window.open('https://caradas.myabsorb.com/#/login', '_blank')
    } else {
      if (invoicePendingToSubmit) {
        submitPendingInvoiceAndNavigate(path)
      } else {
        history.push(path)
      }
    }
  }

  // Package gating (CAR-3710): entries whose module the active center's plan does not include
  // are dropped, for everyone but admins. The routes are guarded too (PrivateRoute renders
  // UpgradeRequired), so this is about not offering a dead end. Gated entries show only once
  // they are known to be allowed for this user (a refresh starts from the cached snapshot).
  // AI Reports also needs the center's admin switch on (CAR-4038).
  const gatedNavigationItems = navigationItems.filter((item) =>
    canUseModule({ user, entitlements, ccId: activeCcId, module: NAV_MODULES[item.path], centerDetail })
  )

  return (
    <AdasBox>
      <AdasBox className='mobile-menu-container'>
        <MobileHamburgerMenu
          navigationItems={gatedNavigationItems}
          handleMenuItemClick={handleMenuItemClick}
          pathName={pathName}
          title={title}
          centerDetail={centerDetail}
          setCenterDetail={setCenterDetail}
          currentUser={user}
        />
      </AdasBox>
      <AdasBox className='desktop-menu-container'>
        <AdasPaper square elevation={0} className={`navigation-bar ${open ? 'expanded' : 'collapsed'}`}>
          {open && (
            <AdasBox className='sidebar-company-info'>
              <CalibrationCenterBar centerDetail={centerDetail} setCenterDetail={setCenterDetail} />
              <AdasTypography className='sidebar-company-location'>
                {centerDetail?.city && centerDetail?.state ? `${centerDetail.city}, ${centerDetail.state}` : ''}
              </AdasTypography>
            </AdasBox>
          )}
          <AdasBox className='menu-list-container'>
            <AdasMenuList
              variant='withoutClickAway'
              onItemClick={handleMenuItemClick}
              pathName={pathName}
              open={open}
              items={gatedNavigationItems}
            />
          </AdasBox>
          {/* Manual collapse/expand toggle — sits just above the footer, replacing the
              old hover-to-expand behavior. `mt-auto` pins it (and the footer) to the
              bottom when the nav items don't fill the height. */}
          <AdasBox
            className={`mt-auto flex items-center border-t border-white/10 py-2 ${open ? 'justify-end px-3' : 'justify-center px-2'}`}
          >
            <button
              type='button'
              onClick={toggleSidebar}
              aria-label={open ? 'Collapse menu' : 'Expand menu'}
              title={open ? 'Collapse menu' : 'Expand menu'}
              className='inline-flex h-9 w-9 items-center justify-center rounded-md text-white/80 transition-colors hover:bg-white/10'
            >
              {open ? <PanelLeftClose className='h-5 w-5' /> : <PanelLeftOpen className='h-5 w-5' />}
            </button>
          </AdasBox>
          {open && (
            <AdasBox className='border-t border-white/10 px-4 py-4 flex flex-col items-center gap-2'>
              <AdasTypography className='text-[10px] uppercase tracking-widest text-white/40'>Powered by</AdasTypography>
              <AdasIntelligenceLogo width={140} />
            </AdasBox>
          )}
        </AdasPaper>
      </AdasBox>
    </AdasBox>
  )
}
